import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import isEqual from 'lodash/isEqual';
import { buyerActions } from 'state/buyer.slice';

export const initialState: CountersTypes.CountersStateType = {
  isLoading: false,
  auctions: {
    diamonds: 0,
    jewelry: 0,
    watches: 0,
  },
  filteredAuctions: {},
  myAuctions: {
    following: 0,
    in_prog: 0,
    won: 0,
    didnt_win: 0,
  },
  filteredMyAuctions: {},
};

const fetchCounters = createAsyncThunk('counters/fetchCounters', async () => {
  const { data }: AxiosResponse<CountersTypes.CountersApiData> = await axios.get(
    '/api/buyers/auctions_counters',
  );
  return data;
});

export const counterSlice = createSlice({
  name: 'counter',
  initialState,
  reducers: {
    updateCount: (
      state,
      action: PayloadAction<{
        type: CountersTypes.AuctionsKeys | CountersTypes.MyActionsKeys;
        count: number;
      }>,
    ) => {
      let { auctions, myAuctions } = state;
      const { type, count } = action.payload;
      if (type in auctions && auctions[type as CountersTypes.AuctionsKeys] !== count) {
        return {
          ...state,
          auctions: {
            ...auctions,
            [type]: count,
          },
        };
      }
      if (type in myAuctions && myAuctions[type as CountersTypes.MyActionsKeys] !== count) {
        return {
          ...state,
          myAuctions: {
            ...myAuctions,
            [type]: count,
          },
        };
      }
      return state;
    },
    updateFilteredCount: (
      state,
      action: PayloadAction<{
        type: CountersTypes.AuctionsKeys | CountersTypes.MyActionsKeys;
        count: number;
      }>,
    ) => {
      let { auctions, filteredAuctions, myAuctions, filteredMyAuctions } = state;
      const { type, count } = action.payload;

      if (type in auctions && filteredAuctions[type as CountersTypes.AuctionsKeys] !== count) {
        return {
          ...state,
          filteredAuctions: {
            ...filteredAuctions,
            [type]: count,
          },
        };
      }
      if (type in myAuctions && filteredMyAuctions[type as CountersTypes.MyActionsKeys] !== count) {
        return {
          ...state,
          filteredMyAuctions: {
            ...filteredMyAuctions,
            [type]: count,
          },
        };
      }
      return state;
    },
    setCount: (
      state,
      action: PayloadAction<{
        type: 'auctions' | 'myAuctions';
        counts: Partial<CountersTypes.AuctionsCounterType | CountersTypes.MyAuctionsCounterType>;
      }>,
    ) => {
      const { type, counts } = action.payload;
      if (!isEqual(state[type], counts)) {
        return {
          ...state,
          [type]: { ...state[type], ...counts },
        };
      }
      return state;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCounters.pending, (state) => {
      return {
        ...state,
        isLoading: true,
      };
    });
    builder.addCase(fetchCounters.fulfilled, (state, action) => {
      const { auctions_count_by_type: auctions, my_auctions_count_by_state: myAuctions } =
        action.payload;

      const counters = {
        auctions: {
          diamonds: auctions.diamonds,
          jewelry: auctions.jewelry,
          watches: auctions.luxury_watches,
        },
        myAuctions: {
          ...state.myAuctions,
          ...myAuctions,
        },
      };
      return { ...state, isLoading: false, ...counters };
    });
    builder.addCase(buyerActions.fetchPersonalizedAuctions.fulfilled, (state, action) => {
      return {
        ...state,
        myAuctions: {
          ...state.myAuctions,
          following: action.payload.watchingAuctions.length,
        },
      };
    });
  },
});

export const countersActions = { ...counterSlice.actions, fetchCounters };

export default counterSlice.reducer;
